<template>
    <div class="py-2 px-2">
        <h1 class="text-2xl text-center font-bold mb-6">Documentation</h1>

        <TabView v-model:activeIndex="activeTabIdx">
            <TabPanel header="Tracking Links">
                <div class="card-bg mb-6 p-4 rounded-lg text-sm">
                    <p class="font-medium mb-1">How to add a tracking link</p>
                    <p>
                        Tracking links come from the affiliate platform's admin panel — one per publisher / site
                        pairing. Once generated, paste the URL into the pending <strong>SiteBrandLink</strong> in
                        AffiliationSpace under <strong>Advertisers → Requested</strong>. The
                        <code>internalNote</code> field auto-fills from the URL for supported systems, and per-system
                        enrichment adds the required postback placeholders.
                    </p>
                </div>

                <MyAffiliatesLinks />
                <ReferOnLinks />
                <CellXpertLinks />
                <SoftswissLinks />
                <AffiseLinks />
                <NetreferLinks />
            </TabPanel>

            <TabPanel header="Postbacks">
                <div class="card-bg mb-6 p-4 rounded-lg text-sm">
                    <p class="font-medium mb-1">How to get postback URLs</p>
                    <p>
                        Go to the <strong>Voonix Advertisers</strong> tab, find the partner you want to set up postbacks for,
                        and click the <i class="pi pi-link" /> icon next to the SALID. This will open a modal with the
                        correct postback URLs pre-filled for that affiliate system and SALID — ready to copy.
                    </p>
                </div>

                <MyAffiliates />
                <ReferOn />
                <CellXpert />
                <Softswiss />
                <Affise />
                <Netrefer />
            </TabPanel>
        </TabView>
    </div>
</template>

<script setup>
import { ref, provide, watch, nextTick, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import MyAffiliates from './MyAffiliates.vue';
import ReferOn from './ReferOn.vue';
import CellXpert from './CellXpert.vue';
import Softswiss from './Softswiss.vue';
import Affise from './Affise.vue';
import Netrefer from './Netrefer.vue';
import MyAffiliatesLinks from './MyAffiliatesLinks.vue';
import ReferOnLinks from './ReferOnLinks.vue';
import CellXpertLinks from './CellXpertLinks.vue';
import SoftswissLinks from './SoftswissLinks.vue';
import AffiseLinks from './AffiseLinks.vue';
import NetreferLinks from './NetreferLinks.vue';

// Deep-link support: ?section=<tab>[-<system>]
//   links               → Tracking Links tab, no scroll
//   links-myaffiliates  → Tracking Links tab, MyAffiliates section expanded + scrolled to
//   postbacks-cellxpert → Postbacks tab, CellXpert expanded + scrolled to
// Descendant components inject `activeHelpSection` (a ref of the current slug),
// match against their own slug, and self-expand + self-mount an id="section-<slug>".
// This file owns tab switching + scroll.
const route = useRoute();
const activeTabIdx = ref(0);
const activeSection = ref('');
provide('activeHelpSection', activeSection);

// Aliases: alternate slugs → canonical section id. Useful when a doc is worth
// linking to under more than one name (e.g. "key-softswiss" and
// "token-softswiss" both point at the Softswiss Statistic-token accordion).
// The canonical slug is what the component actually registered with
// useHelpSection, and what the DOM id uses.
const SECTION_ALIASES = {
    'key-softswiss': 'token-softswiss',
};

const applySectionFromRoute = () => {
    const raw = String(route.query.section || '').toLowerCase();
    const slug = SECTION_ALIASES[raw] || raw;
    activeSection.value = slug;

    // Tab: prefix determines it. Default to Tracking Links.
    if (slug.startsWith('postbacks')) {
        activeTabIdx.value = 1;
    } else {
        activeTabIdx.value = 0;
    }

    // Only scroll when a system is specified (i.e. slug has a "-" component).
    if (!slug.includes('-')) return;

    // Give the reactive expand + tab switch a tick to commit before we scroll.
    nextTick(() => {
        const el = document.getElementById(`section-${slug}`);
        if (el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    });
};

onMounted(applySectionFromRoute);
watch(() => route.query.section, applySectionFromRoute);
</script>

<style scoped>
/* Panel body — transparent so it sits on top of whatever page bg is beneath.
   Body text is a soft off-white so it doesn't clash on pure-black backgrounds.
   <strong class="text-white"> inside content still pops against this. */
:deep(.p-tabview-panels) {
    background-color: transparent !important;
    color: #d1d5db !important;
    padding-top: 1.25rem !important;
}

/* Centered segmented-control style nav. Overrides PrimeVue's stretched underline. */
:deep(.p-tabview-nav-container) {
    display: flex;
    justify-content: center;
}

:deep(.p-tabview-nav) {
    display: inline-flex !important;
    gap: 4px;
    padding: 4px;
    background: rgba(255, 255, 255, 0.04) !important;
    border: 1px solid var(--card-border-color) !important;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

/* Remove default underline / border between tabs. */
:deep(.p-tabview-nav li),
:deep(.p-tabview-nav li .p-tabview-nav-link) {
    border: none !important;
    margin: 0 !important;
}

/* Each tab is a rounded pill. */
:deep(.p-tabview-nav li .p-tabview-nav-link) {
    background-color: transparent !important;
    color: #9ca3af !important;
    padding: 0.55rem 1.5rem !important;
    border-radius: 8px !important;
    font-weight: 500;
    letter-spacing: 0.01em;
    transition: background-color 0.15s ease, color 0.15s ease, transform 0.15s ease;
    cursor: pointer;
}

:deep(.p-tabview-nav li:not(.p-highlight) .p-tabview-nav-link:hover) {
    color: #e5e7eb !important;
    background-color: rgba(255, 255, 255, 0.05) !important;
}

/* Active tab: filled pill in the app's primary color. */
:deep(.p-tabview-nav li.p-highlight .p-tabview-nav-link) {
    color: white !important;
    background: var(--primary-color, #3b82f6) !important;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
}

/* Kill PrimeVue's ink-bar (the animated underline) — we're using a pill instead. */
:deep(.p-tabview-ink-bar) {
    display: none !important;
}

/* Nudge the focus ring so it hugs the pill. */
:deep(.p-tabview-nav li .p-tabview-nav-link:focus) {
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.45) !important;
    outline: none;
}
</style>
